/**
 * Coordination group (#387 / #378 / #380 foundation) — one LeftRail tab that homes
 * the tasking worklists under a sub-tab strip: Arrivals · Incidents · Requests ·
 * Sent requests · Drone requests · Inbox · Information sharing.
 *
 * The strip is horizontally SCROLLABLE (#565): the seven sub-tabs cannot fit the
 * 360px rail at equal width, so each label renders at its natural width (no `flex-1`
 * squish, `whitespace-nowrap`, no truncation) inside an `overflow-x-auto` scroller
 * with scroll-aware edge fades. Founder finding 2026-07-06: the equal-width strip
 * clipped "Drone requests" → "Dr…" and wrapped "Sent requests".
 *
 * W4a (coordination-tab council A.1/A.2) folds the former separate `assist` + `tasks`
 * sub-tabs into ONE "Requests" tab that holds TWO clearly-headed DIRECTED sections —
 * Inbound (`assistance_requests`, municipality/First-Nation → PEOC) and Outbound
 * (`agency_tasks`, PEOC → agency) — NEVER one merged list (the flows are doctrinally
 * distinct; merging re-creates the forward-to-self confusion class). The drone
 * marketplace, which previously held the "Requests" name, is renamed "Drone requests"
 * (sub-tab id `drone-requests`) to free that name for the unified coordination tab.
 *
 * Per design-council PART IX (quiet COP, burden-of-proof on adding, "one control one
 * home"): the coordination surfaces are co-located so the primary rail stays at four
 * tabs (Alerts · Layers · News · Coordination). This is a *visual* group only — each
 * sub-panel renders its own model/API; it does NOT merge the underlying data models.
 *
 * All sub-panels stay MOUNTED (the inactive ones are hidden with CSS, not unmounted —
 * same idiom as StackedQueueTabs + the rail itself) so each keeps polling and its
 * open-count badge stays live regardless of the active sub-tab. Counts are summed and
 * reported up for the primary "Coordination" tab badge (Sent requests excluded — its
 * rows ARE the inbound Assist worklist mirror, so summing both would double-count).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { AgencyTaskView, Incident } from '@tsr/shared-types';
import type { DroneMissionView } from './api';
import { IncidentsPanel } from './IncidentsPanel';
import { PeocArrivalsPanel } from './PeocArrivalsPanel';
import { AssistQueuePanel } from './AssistQueuePanel';
import { MyAssistRequestsPanel } from './MyAssistRequestsPanel';
import { DroneRequestsPanel } from './DroneRequestsPanel';
import { OutboundTasksPanel } from './OutboundTasksPanel';
import { InboxTasksPanel } from './InboxTasksPanel';
import { CommandHandoverInboxPanel } from './CommandHandoverInboxPanel';
import { CommunityCoordinationInboxPanel } from './CommunityCoordinationInboxPanel';
import { COMMUNITY_COORDINATION_TAB_LABEL } from './communityCommsInboxVocabulary';
import { InformationSharingRollup } from './InformationSharingRollup';
import { InteropsThreadPanel } from './InteropsThreadPanel';
import {
  INFORMATION_SHARING_LABEL,
  STANDALONE_THREAD_LABEL,
  STANDALONE_THREAD_SUBTEXT,
} from './interopsVocabulary';
import {
  DRONE_REQUESTS_TAB_LABEL,
  REQUESTS_INBOUND_HEADING,
  REQUESTS_INBOUND_SUBTEXT,
  REQUESTS_OUTBOUND_HEADING,
  REQUESTS_OUTBOUND_SUBTEXT,
  REQUESTS_TAB_LABEL,
  SENT_REQUESTS_TAB_LABEL,
} from './requestsTabVocabulary';
import { PEOC_ARRIVALS_TAB_LABEL } from './peocArrivalsVocabulary';

// W4b — 'arrivals' is FIRST (leftmost): first-notice intake, upstream of tasking.
type SubTab =
  | 'arrivals'
  | 'incidents'
  | 'requests'
  | 'my-requests'
  | 'drone-requests'
  | 'inbox'
  | 'community-coordination'
  | 'info-sharing';

export function CoordinationPanel({
  onSelectIncident,
  onSelectMission,
  onSelectTask,
  onSelectInboxTask,
  onFlyTo,
  onActiveCountChange,
}: {
  /** Open an incident's record (the {kind:'incident'} SidePanel arm). */
  onSelectIncident?: (incident: Incident) => void;
  /** Open a drone mission's lifecycle + live-feed panel ({kind:'mission'} arm). */
  onSelectMission?: (m: DroneMissionView) => void;
  /** Open an OUTBOUND agency task in REQUESTER mode (the {kind:'agencyTask'} arm, #379). */
  onSelectTask?: (task: AgencyTaskView) => void;
  /** Open an INBOX agency task in TARGET mode (#379, Phase 3b-2b round-trip). */
  onSelectInboxTask?: (task: AgencyTaskView) => void;
  /** Re-centre the COP on an incident centroid (reuses flyToIncident). */
  onFlyTo?: (lat: number, lon: number) => void;
  /** Report the aggregate open count up for the primary Coordination tab badge. */
  onActiveCountChange?: (count: number) => void;
}): JSX.Element {
  const [sub, setSub] = useState<SubTab>('incidents');
  // W4b — the PEOC Arrivals inbox: NOT-yet-acknowledged first-notice arrivals.
  const [arrivalsCount, setArrivalsCount] = useState(0);
  const [incidentCount, setIncidentCount] = useState(0);
  // The unified Requests tab's two directed sections report their open counts
  // separately; the tab badge is their sum (inbound assist + outbound tasks).
  const [assistCount, setAssistCount] = useState(0);
  const [tasksCount, setTasksCount] = useState(0);
  // #419 — the requestor's OWN open count, for the "Sent requests" per-tab badge only. It is
  // deliberately NOT folded into the aggregate below: those rows ARE the Assist worklist
  // (the requestor mirror), so summing both would double-count the same requests.
  const [myRequestsCount, setMyRequestsCount] = useState(0);
  const [droneRequestCount, setDroneRequestCount] = useState(0);
  const [inboxCount, setInboxCount] = useState(0);
  // #481 MUST-1 — pending command hand-overs (§4.8 transfers + §4.7 unified invites) addressed to
  // this org. Shares the Inbox tab with the agency-task inbox; both fold into the Inbox badge.
  const [commandHandoverCount, setCommandHandoverCount] = useState(0);
  // #454 — the PEOC's multi-community coordination inbox: the operator's OWN not-yet-acknowledged
  // total, for THIS sub-tab's badge only. Deliberately NOT summed into the aggregate Coordination
  // badge below (rolling it into the primary badge is a NAMED next-slice, #454 scope-out (d)).
  const [communityCoordinationCount, setCommunityCoordinationCount] = useState(0);

  const requestsCount = assistCount + tasksCount;
  // The Inbox tab badge = the agency-task inbox + the command hand-over inbox (both are addressed-to-me).
  const inboxTotal = inboxCount + commandHandoverCount;

  // #565 — the sub-tab strip scrolls horizontally (7 tabs > 360px rail). Track the scroll
  // position so the edge fades appear only when there is more strip to reach in that
  // direction (mirrors useElementSize's ResizeObserver-availability guard for jsdom/SSR).
  const subTabStripRef = useRef<HTMLDivElement>(null);
  const [subTabFade, setSubTabFade] = useState<{ start: boolean; end: boolean }>({
    start: false,
    end: false,
  });
  const recomputeSubTabFade = useCallback(() => {
    const el = subTabStripRef.current;
    if (!el) return;
    const maxScroll = el.scrollWidth - el.clientWidth;
    setSubTabFade({ start: el.scrollLeft > 1, end: el.scrollLeft < maxScroll - 1 });
  }, []);
  useEffect(() => {
    recomputeSubTabFade();
    const el = subTabStripRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => recomputeSubTabFade());
    ro.observe(el);
    return () => ro.disconnect();
  }, [recomputeSubTabFade]);

  useEffect(() => {
    onActiveCountChange?.(
      arrivalsCount + incidentCount + assistCount + tasksCount + droneRequestCount + inboxTotal,
    );
  }, [
    arrivalsCount,
    incidentCount,
    assistCount,
    tasksCount,
    droneRequestCount,
    inboxTotal,
    onActiveCountChange,
  ]);

  return (
    <section
      data-testid="coordination-panel"
      data-active-subtab={sub}
      className="flex h-full min-h-0 flex-col overflow-hidden"
    >
      {/* #565 — the sub-tab strip scrolls horizontally so EVERY label renders in full
          (no `flex-1` squish, no truncation) on the 360px rail; scroll-aware edge fades
          signal the off-screen tabs. */}
      <div className="relative shrink-0 border-b border-slate-800 bg-slate-900/60">
        <div
          ref={subTabStripRef}
          role="tablist"
          onScroll={recomputeSubTabFade}
          className="flex overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {/* W4b — Arrivals FIRST (leftmost): first-notice intake, upstream of tasking. */}
          <SubTabButton
            id="arrivals"
            label={PEOC_ARRIVALS_TAB_LABEL}
            active={sub === 'arrivals'}
            count={arrivalsCount}
            badgeClass="bg-teal-600"
            onSelect={setSub}
          />
          <SubTabButton
            id="incidents"
            label="Incidents"
            active={sub === 'incidents'}
            count={incidentCount}
            badgeClass="bg-sky-600"
            onSelect={setSub}
          />
          <SubTabButton
            id="requests"
            label={REQUESTS_TAB_LABEL}
            active={sub === 'requests'}
            count={requestsCount}
            badgeClass="bg-violet-600"
            onSelect={setSub}
          />
          {/* #565 — the requestor's own sent assistance requests; renamed off "My Requests"
              (founder finding 2026-07-06). The sub-tab id `my-requests` is unchanged. */}
          <SubTabButton
            id="my-requests"
            label={SENT_REQUESTS_TAB_LABEL}
            active={sub === 'my-requests'}
            count={myRequestsCount}
            badgeClass="bg-emerald-600"
            onSelect={setSub}
          />
          <SubTabButton
            id="drone-requests"
            label={DRONE_REQUESTS_TAB_LABEL}
            active={sub === 'drone-requests'}
            count={droneRequestCount}
            badgeClass="bg-cyan-600"
            onSelect={setSub}
          />
          <SubTabButton
            id="inbox"
            label="Inbox"
            active={sub === 'inbox'}
            count={inboxTotal}
            badgeClass="bg-emerald-600"
            onSelect={setSub}
          />
          {/* #454 — the PEOC's MULTI-community coordination inbox (over the #420a threads). Its
              badge = the operator's OWN not-yet-acknowledged total (never a community watchlist);
              the badge renders only at a positive count (an unknown/error is never a "0" badge). */}
          <SubTabButton
            id="community-coordination"
            label={COMMUNITY_COORDINATION_TAB_LABEL}
            active={sub === 'community-coordination'}
            count={communityCoordinationCount}
            badgeClass="bg-cyan-600"
            onSelect={setSub}
          />
          {/* #328p2 — the Liaison's "Information sharing" roll-up (R2 / §2.2). A REFERENCE
              roll-up, not an open-work queue, so it carries no count into the tab-badge sum. */}
          <SubTabButton
            id="info-sharing"
            label={INFORMATION_SHARING_LABEL}
            active={sub === 'info-sharing'}
            count={0}
            badgeClass="bg-sky-600"
            onSelect={setSub}
          />
        </div>
        {/* Scroll-affordance edge fades — visible only when the strip can scroll further
            in that direction (jsdom reports 0 width → both stay hidden, no crash). */}
        <div
          aria-hidden
          data-testid="coordination-subtab-fade-start"
          className={`pointer-events-none absolute inset-y-0 left-0 w-5 bg-gradient-to-r from-slate-900/90 to-transparent transition-opacity ${
            subTabFade.start ? 'opacity-100' : 'opacity-0'
          }`}
        />
        <div
          aria-hidden
          data-testid="coordination-subtab-fade-end"
          className={`pointer-events-none absolute inset-y-0 right-0 w-5 bg-gradient-to-l from-slate-900/90 to-transparent transition-opacity ${
            subTabFade.end ? 'opacity-100' : 'opacity-0'
          }`}
        />
      </div>
      <div className="min-h-0 flex-1 overflow-hidden">
        {/* W4b — PEOC Arrivals: SEPARATE from the Requests tab AND from the urgent
            life-safety queue (a declaration notice is a coordination arrival). */}
        <div
          className={sub === 'arrivals' ? 'h-full overflow-y-auto' : 'hidden'}
          data-testid="arrivals-tab"
        >
          <PeocArrivalsPanel onActiveCountChange={setArrivalsCount} />
        </div>
        <div className={sub === 'incidents' ? 'h-full overflow-y-auto' : 'hidden'}>
          <IncidentsPanel
            onSelectIncident={onSelectIncident}
            onFlyTo={onFlyTo}
            onActiveCountChange={setIncidentCount}
          />
        </div>
        {/* Unified Requests tab — TWO directed sections, never one merged list (A.1). */}
        <div
          className={sub === 'requests' ? 'h-full overflow-y-auto' : 'hidden'}
          data-testid="requests-tab"
        >
          <section data-testid="requests-inbound-section">
            <DirectedSectionHeader
              heading={REQUESTS_INBOUND_HEADING}
              subtext={REQUESTS_INBOUND_SUBTEXT}
              count={assistCount}
              badgeClass="bg-violet-600/80"
            />
            <AssistQueuePanel onActiveCountChange={setAssistCount} />
          </section>
          <section data-testid="requests-outbound-section" className="border-t border-slate-800">
            <DirectedSectionHeader
              heading={REQUESTS_OUTBOUND_HEADING}
              subtext={REQUESTS_OUTBOUND_SUBTEXT}
              count={tasksCount}
              badgeClass="bg-amber-600/80"
            />
            <OutboundTasksPanel onSelectTask={onSelectTask} onActiveCountChange={setTasksCount} />
          </section>
        </div>
        <div className={sub === 'my-requests' ? 'h-full overflow-y-auto' : 'hidden'}>
          <MyAssistRequestsPanel onActiveCountChange={setMyRequestsCount} />
        </div>
        <div className={sub === 'drone-requests' ? 'h-full overflow-y-auto' : 'hidden'}>
          <DroneRequestsPanel
            onSelectMission={onSelectMission}
            onActiveCountChange={setDroneRequestCount}
          />
        </div>
        {/* Inbox — everything addressed to THIS org: #481 pending command hand-overs (§4.8
            transfers + §4.7 unified invites) above the agency-task inbox. The command section
            renders nothing when empty, so the tab reads exactly as before when there are none. */}
        <div className={sub === 'inbox' ? 'h-full overflow-y-auto' : 'hidden'}>
          <CommandHandoverInboxPanel onActiveCountChange={setCommandHandoverCount} />
          <InboxTasksPanel onSelectTask={onSelectInboxTask} onActiveCountChange={setInboxCount} />
        </div>
        {/* #454 — the PEOC's MULTI-community coordination inbox. Stays mounted (CSS-hidden) like
            the siblings so it keeps polling + its badge stays live. Each row opens the real #420a
            thread via selectOnCop({kind:'community'}) — no dead-end. */}
        <div
          className={sub === 'community-coordination' ? 'h-full overflow-y-auto' : 'hidden'}
          data-testid="community-coordination-tab"
        >
          <CommunityCoordinationInboxPanel onActiveCountChange={setCommunityCoordinationCount} />
        </div>
        {/* #328p2 — "Information sharing" (§2.2): the per-incident roll-up (R2) PLUS, beside it,
            a standalone (non-incident) inter-ops thread (#552 / context_type 'standalone'). */}
        <div
          className={sub === 'info-sharing' ? 'flex h-full flex-col overflow-hidden' : 'hidden'}
          data-testid="info-sharing-tab"
        >
          {/* the Liaison's single pane for a scoped incident (inbound + outbound + every out-of-app leg). */}
          <div className="min-h-0 flex-1 overflow-hidden">
            <InformationSharingRollup />
          </div>
          {/* #552 — a thread NOT attached to any incident: share information / request assistance
              without tying it to a tracked incident. The SAME interops surface with context_type
              'standalone' (contextId null). Stays mounted (CSS-hidden) with the rest of the tab. */}
          <section
            data-testid="standalone-interops-section"
            className="shrink-0 max-h-[45%] overflow-y-auto border-t border-slate-800 bg-slate-900/40 px-3 py-2.5"
          >
            <div className="mb-1">
              <h3 className="text-[11px] font-semibold uppercase tracking-wider text-slate-200">
                {STANDALONE_THREAD_LABEL}
              </h3>
              <p className="mt-0.5 text-[10px] leading-snug text-slate-500">
                {STANDALONE_THREAD_SUBTEXT}
              </p>
            </div>
            {/* #565 — this section already carries its own header, so suppress the panel's
                internal "Information sharing" heading (else the label doubles). */}
            <InteropsThreadPanel contextType="standalone" contextId={null} hideHeading />
          </section>
        </div>
      </div>
    </section>
  );
}

/** Header for one directed section of the unified Requests tab (Inbound / Outbound). */
function DirectedSectionHeader({
  heading,
  subtext,
  count,
  badgeClass,
}: {
  heading: string;
  subtext: string;
  count: number;
  badgeClass: string;
}): JSX.Element {
  return (
    <div className="border-b border-slate-800 bg-slate-900/40 px-3 pt-2.5 pb-1.5">
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="text-[11px] font-semibold uppercase tracking-wider text-slate-200">
          {heading}
        </h3>
        {count > 0 && (
          <span
            className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold text-white ${badgeClass}`}
          >
            {count}
          </span>
        )}
      </div>
      <p className="mt-0.5 text-[10px] leading-snug text-slate-500">{subtext}</p>
    </div>
  );
}

function SubTabButton({
  id,
  label,
  active,
  count,
  badgeClass,
  onSelect,
}: {
  id: SubTab;
  label: string;
  active: boolean;
  count: number;
  badgeClass: string;
  onSelect: (tab: SubTab) => void;
}): JSX.Element {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      data-testid={`coordination-subtab-${id}`}
      onClick={() => onSelect(id)}
      className={`flex shrink-0 items-center justify-center gap-1.5 whitespace-nowrap px-2.5 py-2 text-[11px] font-medium ${
        active ? 'text-slate-100 border-b-2 border-sky-400' : 'text-slate-400 hover:text-slate-200'
      }`}
    >
      <span>{label}</span>
      {count > 0 && (
        <span
          data-testid={`coordination-subtab-${id}-badge`}
          className={`rounded-full px-1.5 py-0.5 text-[10px] text-white ${badgeClass}`}
        >
          {count}
        </span>
      )}
    </button>
  );
}
